import React, { useState, useEffect } from 'react';
import { Simulation } from '../../../../types/ombudsman';
import { listSimulations, getEvidenceForSimulation } from '../../../../utils/api';
import { formatTimeAgo } from '../../../../utils/dateUtils';
import { Badge } from '../../../ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../ui/hover-card';
import { cn } from '../../../../lib/utils';

interface SimulationHistoryBarProps {
  signalId: string;
  currentSimulationId: string;
  onNavigate: (simulationId: string) => void;
}

const SimulationHistoryBar: React.FC<SimulationHistoryBarProps> = ({
  signalId,
  currentSimulationId,
  onNavigate,
}) => {
  const [previousSimulations, setPreviousSimulations] = useState<Simulation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPreviousSimulations = async () => {
      if (!signalId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await listSimulations(signalId, 50, 0);
        const simulationsArray = Array.isArray(data) ? data : (data?.simulations || data?.data || []);
        
        // Filter out current simulation and sort by created_at descending (newest first)
        const filtered = simulationsArray
          .filter((sim: Simulation) => sim.id !== currentSimulationId)
          .sort((a: Simulation, b: Simulation) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        
        setPreviousSimulations(filtered);
      } catch (error) {
        console.error('Failed to fetch previous simulations:', error);
        setPreviousSimulations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreviousSimulations();
  }, [signalId, currentSimulationId]);

  // Don't render if there are no previous simulations
  if (isLoading || previousSimulations.length === 0) {
    return null;
  }

  const getOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'upheld':
      case 'partially_upheld':
        return 'bg-orange-500';
      case 'not_upheld':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getOutcomeLabel = (outcome?: string) => {
    switch (outcome) {
      case 'upheld':
        return 'Upheld';
      case 'partially_upheld':
        return 'Partially Upheld';
      case 'not_upheld':
        return 'Not Upheld';
      default:
        return 'Unknown';
    }
  };

  const getConfidence = (simulation: Simulation): number => {
    // Prefer ML prediction confidence if available
    if (simulation.simulation_metadata?.ml_prediction?.probability_upheld) {
      return Math.round(simulation.simulation_metadata.ml_prediction.probability_upheld * 100);
    }
    // Fall back to confidence_score
    return simulation.confidence_score || 0;
  };

  const getOutcome = (simulation: Simulation): string | undefined => {
    // Prefer ML prediction outcome if available
    if (simulation.simulation_metadata?.ml_prediction?.decision_status) {
      return simulation.simulation_metadata.ml_prediction.decision_status;
    }
    // Fall back to predicted_outcome
    return simulation.predicted_outcome;
  };

  // Component for hover card content that fetches evidence
  const SimulationHoverContent: React.FC<{
    simulation: Simulation;
    outcome?: string;
    confidence: number;
  }> = ({ simulation, outcome, confidence }) => {
    const [evidence, setEvidence] = useState<any[]>([]);
    const [isLoadingEvidence, setIsLoadingEvidence] = useState(false);

    useEffect(() => {
      const fetchEvidence = async () => {
        try {
          setIsLoadingEvidence(true);
          const evidenceData = await getEvidenceForSimulation(simulation.id);
          const evidenceArray = Array.isArray(evidenceData) ? evidenceData : (evidenceData?.evidence || evidenceData?.data || []);
          setEvidence(evidenceArray);
        } catch (error) {
          console.error('Failed to fetch evidence for simulation:', error);
          setEvidence([]);
        } finally {
          setIsLoadingEvidence(false);
        }
      };

      fetchEvidence();
    }, [simulation.id]);

    return (
      <div className="space-y-3">
        {/* Key Factors */}
        {simulation.key_factors && simulation.key_factors.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Key Factors:</p>
            <ul className="text-sm text-foreground space-y-1">
              {simulation.key_factors.map((factor, idx) => (
                <li key={idx} className="flex items-start gap-1">
                  <span className="text-muted-foreground">•</span>
                  <span>{factor}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Documents Used */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Documents Used:</p>
          {isLoadingEvidence ? (
            <p className="text-xs text-muted-foreground">Loading...</p>
          ) : evidence.length > 0 ? (
            <ul className="text-sm text-foreground space-y-1">
              {evidence.map((doc: any) => (
                <li key={doc.id} className="flex items-start gap-1">
                  <span className="text-muted-foreground">•</span>
                  <span className="truncate">{doc.filename || doc.name || 'Unknown document'}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">No documents</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full border-b border-border bg-gray-50/50 px-6 py-3">
      {/* <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-muted-foreground">Previous Cases:</span>
      </div> */}
      <div className="flex gap-3 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent pb-2">
        {previousSimulations.map((simulation) => {
          const outcome = getOutcome(simulation);
          const confidence = getConfidence(simulation);
          const timeAgo = formatTimeAgo(simulation.created_at);

          return (
            <HoverCard key={simulation.id}>
              <HoverCardTrigger asChild>
                <button
                  onClick={() => onNavigate(simulation.id)}
                  className={cn(
                    "flex flex-col items-center gap-2 min-w-[120px] p-3 rounded-lg border bg-white hover:bg-gray-50 hover:border-primary transition-colors cursor-pointer text-left"
                  )}
                >
                  {/* Outcome Badge */}
                  <div className="flex items-center gap-2 w-full justify-center">
                    <div className={cn("w-2 h-2 rounded-full", getOutcomeColor(outcome))} />
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-xs font-medium border-0 px-2 py-0.5",
                        outcome === 'upheld' || outcome === 'partially_upheld'
                          ? "text-orange-700 bg-orange-50"
                          : outcome === 'not_upheld'
                          ? "text-green-700 bg-green-50"
                          : "text-gray-700 bg-gray-50"
                      )}
                    >
                      {confidence > 0 ? `${confidence}%` : ''} {getOutcomeLabel(outcome)}
                    </Badge>
                  </div>
                  
                  {/* Time Ago */}
                  <span className="text-xs text-muted-foreground">{timeAgo}</span>
                </button>
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <SimulationHoverContent 
                  simulation={simulation}
                  outcome={outcome}
                  confidence={confidence}
                />
              </HoverCardContent>
            </HoverCard>
          );
        })}
      </div>
    </div>
  );
};

export default SimulationHistoryBar;
